import { hex } from "../shared/color";
import { CELL, EMPTY, GRAVITY, SOLID } from "../shared/defs";
import type { Letters, Particles } from "./entities";
import type { Slab, Slabs } from "./slabs";
import type { World } from "./world";

/**
 * Punchline tuning, in world pixels. `zoomFor` / `layoutFor` and the device
 * pixel ratio only change page reflow and how finely slabs are stamped
 * (`world.Z`). They do not scale these distances, speeds, or radii.
 *
 * The glove runs toward the cursor at `speed`, never farther than `reach`
 * (`reach / speed` ≈ 0.091s). Retract eases with (1-u)².
 * Launch speed falls to 40% at the far edge of the cone (`1 - 0.6 * along/depth`).
 * At most `slabCap` slabs. Their sprites are copied before any of them is carved.
 *
 * Crater owners take `craterDmg`. Other blocks inside `splashR` take
 * `splashDmg * (1 - d / splashR)`. A block larger than `bigBlock` px² takes
 * at most `bigCap` from one hit and `bigStack` from the whole punch, so a
 * page-wide wrapper cannot eat every slab. A slab that smashes a block
 * deals `slabHit` under that same cap, once per block per punch, and the
 * slabs of one punch may finish off at most `slabBreaks` blocks.
 * At most one block breaks per frame; the rest wait in a short queue.
 */
export const PUNCH = {
  cd: 1.0,
  reach: 200,
  speed: 2200,
  retract: 0.22,
  squash: 0.16,
  crater: 60,
  /** Letters inside this radius of the fist are knocked off, past the crater edge. */
  knock: 112,
  cone: Math.PI / 3,
  depth: 150,
  slabMin: 16,
  slabMax: 22,
  slabCap: 12,
  tile: 22,
  launchMin: 700,
  launchMax: 1100,
  spin: 8,
  carveSpeed: 350,
  carveMin: 9,
  carveMax: 14,
  minionDmg: 165,
  minionKnock: 1400,
  minionCarve: 18,
  bossKnock: 260,
  airKick: 180,
  hitStop: 0.06,
  shake: 0.62,
  flash: 0.85,
  craterDmg: 150,
  /** Blocks within this share of `crater` from the fist take `craterDmg`; the rim deals splash. */
  core: 0.7,
  splashDmg: 90,
  splashR: 97,
  bigBlock: 25000,
  bigCap: 150,
  slabHit: 110,
  /** Blocks one punch's slabs may finish off. Further slab hits stop short of a break. */
  slabBreaks: 1,
  /** Longest side over shortest side of a block a slab may break. */
  slabAspect: 4,
  /** Whole punch on one big block. Under the 244 hp of a tall page column. */
  bigStack: 170,
  glove: 44,
  coils: 8,
  amp: 12,
  /** Jagged star plus rings, measured across. */
  burst: 140,
  /**
   * Aftershock: for `crackle` seconds after impact, sparks keep knocking the
   * nearest loose letters off around the crater, `crackleTick` apart, up to
   * `crackleLetters` letters in all. The reach grows from `knock` to `crackleR`
   * over the aftershock, so where text is sparse the ripple travels farther.
   */
  crackle: 1.05,
  crackleTick: 0.075,
  crackleLetters: 40,
  crackleR: 480,
  /** Burning flecks per punch, how long they fall, and how many letters each may set off. */
  flecks: 18,
  fleckLife: 2,
  fleckHits: 5,
} as const;

export interface PunchHost {
  readonly aim: number;
  readonly world: World;
  readonly letters: Letters;
  readonly chunks: Slabs;
  readonly parts: Particles;
  knock(ix: number, iy: number): void;
  sfx(id: string, pitch?: number): void;
  shake(n: number): void;
  markShot(): void;
  freeze(seconds: number): void;
  /** Sync the rig and return the grasp hand. Use when a punch starts. */
  hand(): { x: number; y: number };
  /** Grasp hand from the rig drawStick already posed. Does not integrate again. */
  peekHand(): { x: number; y: number };
  /** Cursor, in world pixels. The spring runs toward this, capped at `reach`. */
  aimAt(): { x: number; y: number };
  /** Full-screen flash. `amount` is 0..1, the same scale as a rocket blast. */
  flash(amount: number): void;
  /** Warm light on the scene around a point, fading over `life` seconds. */
  glow?(x: number, y: number, radius: number, strength: number, life: number): void;
}

export type PunchSweep = (x0: number, y0: number, x1: number, y1: number) => void;

type Phase = "idle" | "out" | "squash" | "back";

/** Bookkeeping for one punch. Slabs keep it after the next punch starts. */
interface PunchRun {
  /** Damage dealt to each block. Big blocks stop at `bigStack`. */
  dealt: Map<number, number>;
  /** Blocks a slab of this punch already hit. */
  slabbed: Set<number>;
  slabBreaks: number;
  /** Blocks this punch broke or queued to break. Its slabs fly through them. */
  doomed: Set<number>;
}

interface PendingHit {
  /** A Journey hop swaps the world; hits queued for the old page are dropped. */
  world: World;
  id: number;
  give: number;
  cos: number;
  sin: number;
  hx: number;
  hy: number;
}

interface PunchSlab {
  run: PunchRun;
  x: number;
  y: number;
  a: number;
  vx: number;
  vy: number;
  av: number;
  canvas: HTMLCanvasElement;
  bw: number;
  bh: number;
  size: number;
  tint: number;
  age: number;
}

interface PunchTile {
  cx: number;
  cy: number;
  size: number;
  along: number;
  across: number;
  left: number;
  top: number;
  c0: number;
  r0: number;
  c1: number;
  r1: number;
  cells: number[];
  set: Set<number>;
  canvas: HTMLCanvasElement | null;
}

interface Burst {
  x: number;
  y: number;
  a: number;
  t: number;
  life: number;
}

interface Crackle {
  world: World;
  x: number;
  y: number;
  a: number;
  t: number;
  next: number;
  left: number;
  /** Burning flecks thrown from the dent. Each sets off the letters it falls through. */
  flecks: Fleck[];
}

interface Fleck {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  hits: number;
}

interface Dust {
  x: number;
  y: number;
  a: number;
  t: number;
  life: number;
  side: number;
}

/** Cuff on the left, thumb on top, fist to the right. 18px wide. */
const GLOVE_ROWS = [
  "..................",
  "......oooo........",
  ".....ohhhho.......",
  "....oohhhhho......",
  "..ooohhhhrrro.....",
  ".owwhrrrrrrrrdo...",
  ".owhrrrrrrrrrrdo..",
  ".owwrrrrrrrrrrrdo.",
  ".owwrrrrrrrrrrrdo.",
  ".owwrrrrrrrrrrddo.",
  ".owwrrrrrrrrrddo..",
  "..ossssssssssso...",
  "...ooooooooooo....",
  "..................",
];

const GLOVE_INK: Record<string, string> = {
  o: "#1a1422",
  h: "#ff8a8a",
  r: "#e23d4a",
  d: "#a8222f",
  w: "#ffffff",
  s: "#1a1422",
};

const SQUASH = [
  { sx: 0.62, sy: 1.32 },
  { sx: 1.2, sy: 0.78 },
  { sx: 0.9, sy: 1.08 },
  { sx: 1, sy: 1 },
];

const SPIKE = [1, 0.68, 0.94, 0.62, 1.08, 0.74, 0.9, 0.58, 1.02, 0.7, 0.86, 0.64];
/** Clown confetti over the impact smoke. */
const CONFETTI = ["#ff4fa3", "#ffe26b", "#3fd0ff", "#7cf26a", "#ffffff", "#b18cff"];
const BURST_COLOR = ["#fff6d8", "#ffe26b", "#ffb03a", "#ff7a32", "#e23d4a", "#a8222f"];

let gloveCanvas: HTMLCanvasElement | null = null;

function gloveSheet(): HTMLCanvasElement {
  if (gloveCanvas) return gloveCanvas;
  const canvas = document.createElement("canvas");
  canvas.width = GLOVE_ROWS[0].length;
  canvas.height = GLOVE_ROWS.length;
  const g = canvas.getContext("2d");
  if (!g) return canvas;
  g.imageSmoothingEnabled = false;
  for (let y = 0; y < GLOVE_ROWS.length; y++) {
    const row = GLOVE_ROWS[y];
    if (row.length !== GLOVE_ROWS[0].length) throw new Error("Punchline glove row");
    for (let x = 0; x < row.length; x++) {
      const ink = GLOVE_INK[row[x]];
      if (!ink) continue;
      g.fillStyle = ink;
      g.fillRect(x, y, 1, 1);
    }
  }
  gloveCanvas = canvas;
  return canvas;
}

/**
 * Hotbar mark, 32×16. Four shaded loops in one coil, welded to a cuff and thumb.
 * The in-game spring stays the thick tube; this is only the slot icon.
 */
const ICON_ROWS = [
  "................................",
  "................................",
  ".ooooooooo.oo...................",
  "ooleoleoleoleo......tttt........",
  "ollellellelleeo....ttttt........",
  "ol.eloeloeloeeo..ootttttto......",
  "eloelooloolo.eoeowwtrrrrro......",
  "l.oleoleoleo.eleowtrrrrrrdo.....",
  "l.oleoleoleo.eewowrrrrrrrrdo....",
  "e.oeaoeaoeao.aoeowrrrrrrrrdo....",
  "eeoeeoeeooeo.aoeowrrrrrrrrdo....",
  "ee.aeoaeoaeoaeoeooooooooooo.....",
  "oeeaeeaeeaeeaeo..ooooooooo......",
  "oeeooeooeooeeo..................",
  ".ooooooooo.oo...................",
  "................................",
];

const ICON_INK: Record<string, string> = {
  o: "#1a1422",
  e: "#8d97a6",
  l: "#e7edf5",
  a: "#3e4652",
  w: "#ffffff",
  t: "#ff8a8a",
  r: "#e23d4a",
  d: "#a8222f",
};

export function paintPunchIcon(g: CanvasRenderingContext2D) {
  g.imageSmoothingEnabled = false;
  for (let y = 0; y < ICON_ROWS.length; y++) {
    const row = ICON_ROWS[y];
    for (let x = 0; x < row.length; x++) {
      const ink = ICON_INK[row[x]];
      if (!ink) continue;
      g.fillStyle = ink;
      g.fillRect(x, y, 1, 1);
    }
  }
}

function stampLine(ctx: CanvasRenderingContext2D, x0: number, y0: number, x1: number, y1: number, color: string, size: number) {
  const len = Math.hypot(x1 - x0, y1 - y0);
  const n = Math.max(1, Math.ceil(len / 2));
  ctx.fillStyle = color;
  const h = size / 2;
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    ctx.fillRect(Math.round(x0 + (x1 - x0) * t - h), Math.round(y0 + (y1 - y0) * t - h), size, size);
  }
}

/**
 * Spring-loaded glove. It runs toward the cursor and stops on solid page.
 * Letters do not stop it. A block that contains the aim point is not a wall
 * at its near skin: the fist strikes at the cursor, up to `reach`.
 * Background fill does not count, or every punch would stop on the first cell.
 */
export class Punchline {
  private phase: Phase = "idle";
  private dist = 0;
  private span: number = PUNCH.reach;
  private phaseT = 0;
  private aim = 0;
  private aimDist = 0;
  private aimOwner = -1;
  private startOwner = -1;
  private leftBirth = true;
  private ox = 0;
  private oy = 0;
  private cd = 0;
  private smackT = 0;
  private run: PunchRun = { dealt: new Map(), slabbed: new Set(), slabBreaks: 0, doomed: new Set() };
  /** Breaks left this frame. Detaching a block copies canvases, so one per frame. */
  private breakBudget = 1;
  private pending: PendingHit[] = [];
  private slabs: PunchSlab[] = [];
  private bursts: Burst[] = [];
  private dusts: Dust[] = [];
  private crackles: Crackle[] = [];

  constructor(private host: PunchHost, private sweep: PunchSweep) {}

  get cooling(): number {
    return this.cd;
  }

  meter(): number {
    return this.cd > 0 ? 1 - this.cd / PUNCH.cd : 1;
  }

  busy(): boolean {
    return this.phase !== "idle";
  }

  probe() {
    return { out: this.phase !== "idle", phase: this.phase, dist: this.dist, cd: this.cd, slabs: this.slabs.length, pending: this.pending.length };
  }

  fire() {
    if (this.phase !== "idle" || this.cd > 0) return;
    const hand = this.host.hand();
    const at = this.host.aimAt();
    this.ox = hand.x;
    this.oy = hand.y;
    this.aim = this.host.aim;
    const along = (at.x - hand.x) * Math.cos(this.aim) + (at.y - hand.y) * Math.sin(this.aim);
    this.aimDist = Math.max(0, Math.min(PUNCH.reach, along));
    this.aimOwner = this.ownerNear(hand.x + Math.cos(this.aim) * this.aimDist, hand.y + Math.sin(this.aim) * this.aimDist);
    this.startOwner = this.ownerAt(hand.x, hand.y);
    this.leftBirth = !this.solidAt(hand.x, hand.y);
    this.run = { dealt: new Map(), slabbed: new Set(), slabBreaks: 0, doomed: new Set() };
    this.phase = "out";
    this.dist = 0;
    this.span = PUNCH.reach;
    this.phaseT = 0;
    this.cd = PUNCH.cd;
    this.host.sfx("punch");
    this.host.markShot();
  }

  step(dt: number) {
    this.breakBudget = 1;
    this.flushPending();
    this.cd = Math.max(0, this.cd - dt);
    this.smackT = Math.max(0, this.smackT - dt);
    this.advance(dt);
    this.stepSlabs(dt);
    this.stepFx(dt);
    this.stepCrackles(dt);
  }

  /** A launched minion lands. Carve stays in world pixels, same as the punch. */
  dig(x: number, y: number, radius: number) {
    this.popCircle(x, y, radius, 0, -80);
    const world = this.host.world;
    world.beginCarve();
    world.carve(x, y, radius, 0.2);
    const edge = world.rim(2);
    world.char(edge.cells, edge.ring);
  }

  drawDebris(ctx: CanvasRenderingContext2D) {
    if (!this.slabs.length) return;
    ctx.save();
    ctx.imageSmoothingEnabled = false;
    for (const slab of this.slabs) {
      ctx.save();
      ctx.translate(slab.x, slab.y);
      ctx.rotate(slab.a);
      ctx.drawImage(slab.canvas, -slab.bw / 2, -slab.bh / 2, slab.bw, slab.bh);
      ctx.restore();
    }
    ctx.restore();
  }

  drawFront(ctx: CanvasRenderingContext2D) {
    ctx.save();
    ctx.imageSmoothingEnabled = false;
    if (this.phase !== "idle") this.drawGlove(ctx);
    for (const burst of this.bursts) this.drawBurst(ctx, burst);
    for (const dust of this.dusts) this.drawDust(ctx, dust);
    this.drawFlecks(ctx);
    ctx.restore();
  }

  private advance(dt: number) {
    if (this.phase === "out") this.extend(dt);
    else if (this.phase === "squash") {
      this.phaseT += dt;
      if (this.phaseT >= PUNCH.squash) this.enterBack();
    } else if (this.phase === "back") {
      this.phaseT += dt;
      const u = Math.min(1, this.phaseT / PUNCH.retract);
      this.dist = this.span * (1 - u) * (1 - u);
      if (u >= 1) {
        this.phase = "idle";
        this.dist = 0;
      }
    }
  }

  private extend(dt: number) {
    let left = PUNCH.speed * dt;
    const cos = Math.cos(this.aim);
    const sin = Math.sin(this.aim);
    while (left > 0 && this.phase === "out") {
      const hop = Math.min(6, left);
      const next = Math.min(PUNCH.reach, this.dist + hop);
      const x0 = this.ox + cos * this.dist;
      const y0 = this.oy + sin * this.dist;
      const x1 = this.ox + cos * next;
      const y1 = this.oy + sin * next;
      this.sweep(x0, y0, x1, y1);
      const hit = this.terrain(x0, y0, x1, y1);
      if (hit) {
        this.dist = Math.hypot(hit.x - this.ox, hit.y - this.oy);
        this.impact(hit.x, hit.y);
        return;
      }
      this.dist = next;
      left -= hop;
      if (this.aimDist >= 12 && this.aimOwner >= 0 && this.dist >= this.aimDist - 0.01) {
        const x = this.ox + cos * this.aimDist;
        const y = this.oy + sin * this.aimDist;
        this.dist = this.aimDist;
        this.impact(x, y);
        return;
      }
      if (this.dist >= PUNCH.reach - 0.01) {
        this.whiff();
        return;
      }
    }
  }

  private terrain(x0: number, y0: number, x1: number, y1: number): { x: number; y: number } | null {
    const dx = x1 - x0;
    const dy = y1 - y0;
    const len = Math.hypot(dx, dy);
    const n = Math.max(1, Math.ceil(len / 4));
    for (let i = 1; i <= n; i++) {
      const x = x0 + (dx * i) / n;
      const y = y0 + (dy * i) / n;
      const dist = Math.hypot(x - this.ox, y - this.oy);
      if (this.rayHit(x, y, dist)) return { x, y };
    }
    return null;
  }

  private cellIndex(x: number, y: number): number {
    const world = this.host.world;
    if (x < 0 || y < 0 || x >= world.W || y >= world.H) return -1;
    const col = Math.floor(x / CELL);
    const row = Math.floor(y / CELL);
    if (col < 0 || row < 0 || col >= world.GW || row >= world.GH) return -1;
    return row * world.GW + col;
  }

  /** Solid blocks and debris. Letters and the page's background fill are not a hit. */
  private solidAt(x: number, y: number): boolean {
    const i = this.cellIndex(x, y);
    return i >= 0 && this.host.world.mat[i] >= SOLID;
  }

  private ownerAt(x: number, y: number): number {
    const i = this.cellIndex(x, y);
    return i < 0 ? -1 : this.host.world.owner[i];
  }

  /** Owner of the aimed cell, or of solid page within a few pixels of a padded click. */
  private ownerNear(x: number, y: number): number {
    if (this.solidAt(x, y)) return this.ownerAt(x, y);
    for (let ring = 6; ring <= 18; ring += 6) {
      for (let k = 0; k < 8; k++) {
        const a = (k / 8) * Math.PI * 2;
        const ox = x + Math.cos(a) * ring;
        const oy = y + Math.sin(a) * ring;
        if (this.solidAt(ox, oy)) return this.ownerAt(ox, oy);
      }
    }
    return -1;
  }

  /**
   * First solid surface along the spring. Like a rocket, the glove lands on the
   * cursor when the cursor is on solid page within `reach`: nothing on the way
   * stops it. Past an open-air cursor it stops on the first solid page, except
   * the block under the fist at launch.
   */
  private rayHit(x: number, y: number, dist: number): boolean {
    if (!this.solidAt(x, y)) {
      if (dist > 6) this.leftBirth = true;
      return false;
    }
    if (this.aimOwner >= 0 && dist + 3 < this.aimDist) return false;
    const owner = this.ownerAt(x, y);
    if (!this.leftBirth && (owner === this.startOwner || owner < 0)) return false;
    return true;
  }

  private whiff() {
    this.dist = PUNCH.reach;
    this.span = PUNCH.reach;
    const cos = Math.cos(this.aim);
    const sin = Math.sin(this.aim);
    this.host.knock(-cos * PUNCH.airKick, -sin * PUNCH.airKick);
    this.enterBack();
  }

  private impact(x: number, y: number) {
    this.phase = "squash";
    this.phaseT = 0;
    this.span = this.dist;
    this.host.sfx("thud");
    this.host.sfx("horn");
    this.host.shake(PUNCH.shake);
    this.host.flash(PUNCH.flash);
    this.host.freeze(PUNCH.hitStop);
    this.bursts.push({ x, y, a: this.aim, t: 0, life: 0.36 });
    this.dusts.push({ x, y, a: this.aim, t: 0, life: 0.46, side: 1 });
    this.dusts.push({ x, y, a: this.aim, t: 0, life: 0.46, side: -1 });
    this.dusts.push({ x, y, a: this.aim, t: 0, life: 0.38, side: 0 });
    this.dusts.push({ x, y, a: this.aim + 0.6, t: 0, life: 0.34, side: 1 });
    this.linger(x, y);
    for (let i = 0; i < 8; i++) {
      const a = this.aim + (i / 8) * Math.PI * 2;
      const sp = 160 + (i % 3) * 90;
      this.host.parts.spawn({
        x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 40,
        life: 0.32, max: 0.32, size: 2.4,
        color: i % 2 === 0 ? "#ffe26b" : "#ff8a2a",
        grav: 420, rubble: false, glow: true, kind: "spark",
      });
    }
    for (let n = 0; n < 4; n++) {
      const a = this.aim + (n - 1.5) * 0.45;
      const sp = 40 + Math.random() * 70;
      this.host.parts.spawn({
        x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 20,
        life: 0.2, max: 0.2, size: 8 + Math.random() * 6,
        color: "#ff8a2a", grav: -140, rubble: false, glow: true, kind: "fireball",
      });
    }
    // The impact frame already copies slabs and carves; the first block breaks next frame.
    this.breakBudget = 0;
    this.breakPage(x, y);
  }

  /**
   * What stays after the star, for most of a second: a plaster dust cloud that
   * rolls out from the dent, a ring of fire puffs, burning chunks, clown
   * confetti, sparks and embers, and a warm light on the page around the dent.
   */
  private linger(x: number, y: number) {
    const parts = this.host.parts;
    const R = PUNCH.crater;
    const back = this.aim + Math.PI;
    this.host.glow?.(x, y, R * 3.6, 0.75, 0.8);
    this.host.glow?.(x, y, R * 1.8, 0.7, 0.1);
    this.host.world.scorch(x, y, R * 0.9, R * 1.75, 0.85);
    // Dust shock: pale plaster rolling outward, then drifting up for 1-2s.
    for (let n = 0; n < 14; n++) {
      const a = n < 9 ? back + (Math.random() - 0.5) * 3 : Math.random() * Math.PI * 2;
      const d = R * (0.2 + Math.random() * 0.7);
      const sp = 60 + Math.random() * 150;
      parts.smoke(
        x + Math.cos(a) * d, y + Math.sin(a) * d,
        Math.cos(a) * sp, Math.sin(a) * sp * 0.7 - 50,
        12 + Math.random() * 8, 1.1 + Math.random() * 1.1, n % 3 === 0 ? "#fff0d8" : n % 3 === 1 ? "#d9b48a" : "#b98e66",
      );
    }
    for (let n = 0; n < 4; n++) {
      const a = Math.random() * Math.PI * 2;
      const d = R * (0.1 + Math.random() * 0.4);
      parts.smoke(
        x + Math.cos(a) * d, y + Math.sin(a) * d,
        Math.cos(a) * 50, Math.sin(a) * 30 - 60,
        8 + Math.random() * 5, 1.4 + Math.random() * 1, "#2a2230",
      );
    }
    const fireball = (px: number, py: number, vx: number, vy: number, life: number, size: number, puff: 0 | 1 | 2) => {
      parts.spawn({
        x: px, y: py, vx, vy, life, max: life, size,
        color: "#ff8a2a", grav: -190, rubble: false, glow: true, kind: "fireball", puff,
      });
    };
    // Fire ring: puffs thrown out around the dent, bigger toward the back.
    for (let n = 0; n < 32; n++) {
      const a = (n / 32) * Math.PI * 2 + Math.random() * 0.3;
      const d = R * (0.15 + Math.random() * 0.45);
      const toward = 0.75 + 0.25 * Math.cos(a - back);
      const sp = (120 + Math.random() * 320) * toward;
      fireball(
        x + Math.cos(a) * d, y + Math.sin(a) * d,
        Math.cos(a) * sp, Math.sin(a) * sp * 0.8 - 70,
        0.55 + Math.random() * 0.6, R * (0.2 + Math.random() * 0.18) * toward,
        Math.random() < 0.4 ? 2 : 0,
      );
    }
    for (let n = 0; n < 5; n++) {
      fireball(x + (Math.random() - 0.5) * R * 0.6, y, (Math.random() - 0.5) * 50, -200 - Math.random() * 160, 0.8 + Math.random() * 0.5, R * (0.24 + Math.random() * 0.12), Math.random() < 0.6 ? 2 : 0);
    }
    // Burning chunks trail fire as they fall.
    for (let n = 0; n < 10; n++) {
      const a = back + (Math.random() - 0.5) * 2.4;
      const sp = 360 + Math.random() * 420;
      const life = 0.7 + Math.random() * 0.7;
      parts.spawn({
        x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 160,
        life, max: life, size: 3.2,
        color: "#ffe26b", grav: GRAVITY * 0.8, rubble: false, glow: true, kind: "chunk",
      });
    }
    // Clown confetti: the punchline's own signature over the smoke.
    for (let n = 0; n < 34; n++) {
      const a = back + (Math.random() - 0.5) * 3.2;
      const sp = 120 + Math.random() * 260;
      const life = 0.9 + Math.random() * 0.7;
      parts.spawn({
        x: x + (Math.random() - 0.5) * 20, y: y + (Math.random() - 0.5) * 20,
        vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 220,
        life, max: life, size: 2.5 + Math.random() * 1.5,
        color: CONFETTI[n % CONFETTI.length], grav: 520, rubble: false, glow: false, kind: "crumb",
      });
    }
    for (let n = 0; n < 60; n++) {
      const a = Math.random() * Math.PI * 2;
      const sp = 300 + Math.random() * 760;
      const life = 0.2 + Math.random() * 0.5;
      parts.spawn({
        x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 140,
        life, max: life, size: 1.2,
        color: "#ffe26b", grav: 800, rubble: false, glow: true, kind: "spark", buf: "fire",
      });
    }
    for (let n = 0; n < 40; n++) {
      const a = Math.random() * Math.PI * 2;
      const sp = 60 + Math.random() * 280;
      const life = 1.1 + Math.random() * 1.6;
      parts.spawn({
        x: x + Math.cos(a) * 14, y: y + Math.sin(a) * 14,
        vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 140,
        life, max: life, size: 1,
        color: "#ff8a2a", grav: 0, rubble: false, glow: true, kind: "ember",
      });
    }
    const flecks: Fleck[] = [];
    for (let n = 0; n < PUNCH.flecks; n++) {
      const a = back + (Math.random() - 0.5) * 3;
      const sp = 120 + Math.random() * 340;
      flecks.push({ x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp * 0.6 - 160, life: PUNCH.fleckLife * (0.7 + Math.random() * 0.3), hits: PUNCH.fleckHits });
    }
    this.crackles.push({ world: this.host.world, x, y, a: this.aim, t: 0, next: PUNCH.crackleTick, left: PUNCH.crackleLetters, flecks });
  }

  /**
   * Aftershock. Every tick a spark pops near the dent and knocks the closest
   * loose letters off, charred, so the dent keeps crumbling for about a second.
   */
  private stepCrackles(dt: number) {
    const world = this.host.world;
    for (let i = this.crackles.length - 1; i >= 0; i--) {
      const c = this.crackles[i];
      c.t += dt;
      if (c.world !== world || (c.t >= PUNCH.crackle && !c.flecks.length)) {
        this.crackles.splice(i, 1);
        continue;
      }
      this.stepFlecks(c, dt);
      while (c.t >= c.next && c.next < PUNCH.crackle) {
        const ticksLeft = Math.max(1, Math.round((PUNCH.crackle - c.next) / PUNCH.crackleTick));
        c.next += PUNCH.crackleTick;
        this.crackleTick(c, Math.min(c.left, Math.ceil(c.left / ticksLeft)));
      }
    }
  }

  /** Flecks fall through the page's front, not into it, and char the letters they cross. */
  private stepFlecks(c: Crackle, dt: number) {
    const letters = this.host.letters;
    const parts = this.host.parts;
    const H = this.host.world.H;
    for (let i = c.flecks.length - 1; i >= 0; i--) {
      const f = c.flecks[i];
      f.life -= dt;
      f.vy += 900 * dt;
      f.vx -= f.vx * 0.6 * dt;
      f.x += f.vx * dt;
      f.y += f.vy * dt;
      if (f.life <= 0 || f.hits <= 0 || f.y > H + 40) {
        c.flecks.splice(i, 1);
        continue;
      }
      if (Math.random() < 0.5) {
        const life = 0.25 + Math.random() * 0.25;
        parts.spawn({
          x: f.x, y: f.y, vx: (Math.random() - 0.5) * 30, vy: -20,
          life, max: life, size: 1, color: "#ff8a2a", grav: 0, rubble: false, glow: true, kind: "ember",
        });
      }
      if (c.left <= 0) continue;
      letters.query(f.x - 8, f.y - 8, f.x + 8, f.y + 8, (n) => {
        if (f.hits <= 0 || c.left <= 0 || !letters.alive[n]) return;
        const vx = f.vx * 0.3 + (Math.random() - 0.5) * 120;
        const vy = -120 - Math.random() * 160;
        const burn = 0.2 + Math.random() * 0.2;
        if (!letters.detach(n, vx, vy, burn, false, { vx, vy, av: (Math.random() - 0.5) * 30, burn, pinned: false })) return;
        f.hits--;
        c.left--;
        for (let k = 0; k < 3; k++) {
          const sa = Math.random() * Math.PI * 2;
          const ss = 80 + Math.random() * 180;
          const sl = 0.15 + Math.random() * 0.2;
          parts.spawn({
            x: f.x, y: f.y, vx: Math.cos(sa) * ss, vy: Math.sin(sa) * ss - 60,
            life: sl, max: sl, size: 1.2, color: "#ffe26b", grav: 800, rubble: false, glow: true, kind: "spark", buf: "fire",
          });
        }
      });
    }
  }

  private drawFlecks(ctx: CanvasRenderingContext2D) {
    for (const c of this.crackles) {
      for (const f of c.flecks) {
        const sp = Math.hypot(f.vx, f.vy) || 1;
        const tx = f.x - (f.vx / sp) * Math.min(10, sp * 0.02);
        const ty = f.y - (f.vy / sp) * Math.min(10, sp * 0.02);
        stampLine(ctx, tx, ty, f.x, f.y, "#ff8a2a", 2);
        ctx.fillStyle = "#1a1422";
        ctx.fillRect(Math.round(f.x - 2.5), Math.round(f.y - 2.5), 5, 5);
        ctx.fillStyle = "#fff3a6";
        ctx.fillRect(Math.round(f.x - 1.5), Math.round(f.y - 1.5), 3, 3);
      }
    }
  }

  private crackleTick(c: Crackle, take: number) {
    const letters = this.host.letters;
    const parts = this.host.parts;
    const grow = Math.min(1, c.t / PUNCH.crackle);
    const R = PUNCH.knock + (PUNCH.crackleR - PUNCH.knock) * grow;
    const near: { i: number; d: number }[] = [];
    if (take > 0) {
      letters.query(c.x - R, c.y - R, c.x + R, c.y + R, (i) => {
        if (!letters.alive[i]) return;
        const d = Math.hypot(letters.x[i] + letters.w[i] / 2 - c.x, letters.y[i] + letters.h[i] / 2 - c.y);
        if (d <= R) near.push({ i, d });
      });
      near.sort((p, q) => p.d - q.d);
    }
    const hits = near.slice(0, take);
    for (const { i } of hits) {
      const lx = letters.x[i] + letters.w[i] / 2;
      const ly = letters.y[i] + letters.h[i] / 2;
      const a = Math.atan2(ly - c.y, lx - c.x);
      const sp = 220 + Math.random() * 260;
      const vx = Math.cos(a) * sp + (Math.random() - 0.5) * 80;
      const vy = Math.sin(a) * sp - 180 - Math.random() * 120;
      const burn = 0.05 + Math.random() * 0.2;
      if (!letters.detach(i, vx, vy, burn, false, { vx, vy, av: (Math.random() - 0.5) * 30, burn, pinned: false })) continue;
      c.left--;
      for (let k = 0; k < 3; k++) {
        const sa = a + (Math.random() - 0.5) * 1.6;
        const ss = 120 + Math.random() * 260;
        const life = 0.15 + Math.random() * 0.25;
        parts.spawn({
          x: lx, y: ly, vx: Math.cos(sa) * ss, vy: Math.sin(sa) * ss - 80,
          life, max: life, size: 1.2,
          color: "#ffe26b", grav: 800, rubble: false, glow: true, kind: "spark", buf: "fire",
        });
      }
    }
    // The ripple front: a ragged ring of sparks running out with the reach.
    for (let k = 0; k < 8; k++) {
      const sa = (k / 8) * Math.PI * 2 + Math.random() * 0.7;
      const sr = R * (0.92 + Math.random() * 0.12);
      const sl = 0.12 + Math.random() * 0.12;
      parts.spawn({
        x: c.x + Math.cos(sa) * sr, y: c.y + Math.sin(sa) * sr,
        vx: Math.cos(sa) * 260, vy: Math.sin(sa) * 260 - 40,
        life: sl, max: sl, size: 1.2,
        color: "#ffe26b", grav: 300, rubble: false, glow: true, kind: "spark", buf: "fire",
      });
    }
    // A small pop on the rim every tick keeps the dent alive even with no text left.
    const fade = 1 - c.t / PUNCH.crackle;
    const ra = c.a + Math.PI + (Math.random() - 0.5) * 4;
    const rd = PUNCH.crater * (0.6 + Math.random() * 0.7);
    const px = c.x + Math.cos(ra) * rd;
    const py = c.y + Math.sin(ra) * rd;
    const life = 0.3 + Math.random() * 0.25;
    parts.spawn({
      x: px, y: py, vx: Math.cos(ra) * 40, vy: -60 - Math.random() * 60,
      life, max: life, size: PUNCH.crater * (0.14 + 0.12 * fade),
      color: "#ff8a2a", grav: -190, rubble: false, glow: true, kind: "fireball", puff: 1,
    });
    for (let k = 0; k < 4; k++) {
      const sa = Math.random() * Math.PI * 2;
      const ss = 140 + Math.random() * 300;
      const sl = 0.15 + Math.random() * 0.3;
      parts.spawn({
        x: px, y: py, vx: Math.cos(sa) * ss, vy: Math.sin(sa) * ss - 100,
        life: sl, max: sl, size: 1.2,
        color: "#fff3a6", grav: 800, rubble: false, glow: true, kind: "spark", buf: "fire",
      });
    }
  }

  private enterBack() {
    this.phase = "back";
    this.phaseT = 0;
    this.span = Math.max(this.dist, 1);
    this.host.sfx("boing");
  }

  private breakPage(hx: number, hy: number) {
    const world = this.host.world;
    const cos = Math.cos(this.aim);
    const sin = Math.sin(this.aim);
    const reach = Math.max(PUNCH.depth + PUNCH.crater, PUNCH.knock);
    this.popBox(hx - reach, hy - reach, hx + reach, hy + reach, (lx, ly) => this.inPunch(lx, ly, hx, hy), cos * 980, sin * 980 - 160);
    const tiles = this.collectTiles(hx, hy, cos, sin);
    this.stampAll(tiles.filter((tile) => tile.cells.length >= 8));
    const owners = new Set<number>();
    world.beginCarve();
    world.carve(hx, hy, PUNCH.crater, 0.22, (_cx, _cy, _color, _mat, owner) => {
      if (owner >= 0) owners.add(owner);
    });
    for (const tile of tiles) {
      world.carveWhere(tile.c0, tile.r0, tile.c1, tile.r1, (x, y) => tile.set.has(y * world.GW + x));
    }
    const edge = world.rim(2);
    world.char(edge.cells, edge.ring);
    for (const tile of tiles) {
      if (tile.canvas) this.launchTile(tile);
    }
    const splashR = PUNCH.splashR;
    const splash = (dist: number) => PUNCH.splashDmg * (1 - dist / splashR);
    world.blocks.forEach((block, index) => {
      if (!block.alive) return;
      const dx = Math.max(block.x - hx, 0, hx - (block.x + block.w));
      const dy = Math.max(block.y - hy, 0, hy - (block.y + block.h));
      const dist = Math.hypot(dx, dy);
      // The crater's ragged rim only grazes a block whose edge is far from the fist.
      if (owners.has(index)) this.hurtBlock(index, dist <= PUNCH.crater * PUNCH.core ? PUNCH.craterDmg : splash(dist), hx, hy);
      else if (dist < splashR) this.hurtBlock(index, splash(dist), hx, hy);
    });
  }

  private inPunch(x: number, y: number, hx: number, hy: number): boolean {
    if (Math.hypot(x - hx, y - hy) <= PUNCH.knock) return true;
    const dx = x - hx;
    const dy = y - hy;
    const cos = Math.cos(this.aim);
    const sin = Math.sin(this.aim);
    const along = dx * cos + dy * sin;
    const across = -dx * sin + dy * cos;
    if (along < -4 || along > PUNCH.depth + 8) return false;
    const limit = Math.max(10, along * Math.tan(PUNCH.cone / 2)) + 8;
    return Math.abs(across) <= limit;
  }

  /**
   * One hit. Page-wide blocks take at most `bigCap` from this call and
   * `bigStack` across the crater, the splash, and every slab of this punch.
   * A slab counts once per block, and only `slabBreaks` slab hits per punch
   * may finish a block. A hit that would break a block after this frame's
   * break waits in `pending` for a later frame.
   */
  private hurtBlock(id: number, amount: number, hx: number, hy: number, run: PunchRun = this.run, slab = false) {
    if (amount <= 0) return;
    const world = this.host.world;
    const block = world.blocks[id];
    if (!block?.alive || block.lost >= block.cells) return;
    if (slab) {
      if (run.slabbed.has(id)) return;
      run.slabbed.add(id);
    }
    let give = amount;
    const already = run.dealt.get(id) ?? 0;
    if (block.w * block.h > PUNCH.bigBlock) {
      give = Math.min(amount, PUNCH.bigCap, PUNCH.bigStack - already);
      if (give <= 0) return;
    }
    const owed = this.owed(id);
    let breaks = this.breaks(id, give + owed);
    if (breaks && slab) {
      if (run.slabBreaks >= PUNCH.slabBreaks || !this.compact(block)) {
        if (block.lost >= block.cells * 0.38) return;
        give = Math.max(0, block.hp - block.damage - owed - 1);
        if (give <= 0) return;
        breaks = false;
      } else run.slabBreaks++;
    }
    run.dealt.set(id, already + give);
    const cos = Math.cos(this.aim);
    const sin = Math.sin(this.aim);
    if (breaks) run.doomed.add(id);
    if (breaks && (this.breakBudget <= 0 || owed > 0)) {
      this.pending.push({ world, id, give, cos, sin, hx, hy });
      return;
    }
    if (breaks) this.breakBudget--;
    world.damageBlock(id, give, cos, sin, hx, hy, hx, hy);
  }

  /** A card or image a chunk can snap. Long strips such as nav bars only crack. */
  private compact(block: { w: number; h: number }): boolean {
    return Math.max(block.w, block.h) <= PUNCH.slabAspect * Math.max(1, Math.min(block.w, block.h));
  }

  /** Damage already queued for this block. */
  private owed(id: number): number {
    let sum = 0;
    const world = this.host.world;
    for (const hit of this.pending) if (hit.id === id && hit.world === world) sum += hit.give;
    return sum;
  }

  /** Would `amount` more damage detach this block? Mirrors `World.damageBlock`. */
  private breaks(id: number, amount: number): boolean {
    const block = this.host.world.blocks[id];
    if (!block?.alive || !block.canDetach) return false;
    return block.damage + amount >= block.hp || block.lost >= block.cells * 0.38;
  }

  /** Apply queued hits. Only the first that breaks a block runs this frame. */
  private flushPending() {
    const world = this.host.world;
    while (this.pending.length) {
      const hit = this.pending[0];
      const block = hit.world === world ? world.blocks[hit.id] : null;
      if (!block?.alive) {
        this.pending.shift();
        continue;
      }
      const breaks = this.breaks(hit.id, hit.give);
      if (breaks && this.breakBudget <= 0) return;
      this.pending.shift();
      if (breaks) this.breakBudget--;
      world.damageBlock(hit.id, hit.give, hit.cos, hit.sin, hit.hx, hit.hy, hit.hx, hit.hy);
    }
  }

  /** Read slab footprints while the page is still intact. Carving happens after every snapshot. */
  private collectTiles(hx: number, hy: number, cos: number, sin: number): PunchTile[] {
    const world = this.host.world;
    const px = -sin;
    const py = cos;
    const halfTan = Math.tan(PUNCH.cone / 2);
    const tiles: PunchTile[] = [];
    for (let along = 30; along <= PUNCH.depth && tiles.length < PUNCH.slabCap; along += PUNCH.tile) {
      const halfW = along * halfTan;
      for (let across = -halfW; across <= halfW + 0.01 && tiles.length < PUNCH.slabCap; across += PUNCH.tile) {
        const jx = (Math.random() - 0.5) * 8;
        const jy = (Math.random() - 0.5) * 8;
        const cx = hx + cos * along + px * across + jx;
        const cy = hy + sin * along + py * across + jy;
        const size = PUNCH.slabMin + Math.random() * (PUNCH.slabMax - PUNCH.slabMin);
        const left = cx - size / 2;
        const top = cy - size / 2;
        const c0 = Math.floor(left / CELL);
        const r0 = Math.floor(top / CELL);
        const c1 = Math.ceil((cx + size / 2) / CELL) - 1;
        const r1 = Math.ceil((cy + size / 2) / CELL) - 1;
        const cells: number[] = [];
        for (let row = r0; row <= r1; row++) {
          if (row < 0 || row >= world.GH) continue;
          for (let col = c0; col <= c1; col++) {
            if (col < 0 || col >= world.GW) continue;
            const i = row * world.GW + col;
            if (world.mat[i] === EMPTY) continue;
            cells.push(i);
          }
        }
        if (!cells.length) continue;
        tiles.push({
          cx, cy, size, along, across, left, top, c0, r0, c1, r1,
          cells, set: new Set(cells), canvas: null,
        });
      }
    }
    return tiles;
  }

  private launchTile(tile: PunchTile) {
    const canvas = tile.canvas;
    if (!canvas) return;
    const world = this.host.world;
    const fall = 1 - 0.6 * Math.min(1, tile.along / PUNCH.depth);
    const speed = (PUNCH.launchMin + Math.random() * (PUNCH.launchMax - PUNCH.launchMin)) * fall;
    const ang = this.aim + Math.max(-0.28, Math.min(0.28, tile.across / PUNCH.depth));
    this.slabs.push({
      run: this.run,
      x: tile.cx,
      y: tile.cy,
      a: (Math.random() - 0.5) * 0.4,
      vx: Math.cos(ang) * speed,
      vy: Math.sin(ang) * speed,
      av: (Math.random() * 2 - 1) * PUNCH.spin,
      canvas,
      bw: tile.size,
      bh: tile.size,
      size: tile.size,
      tint: world.colorAt(tile.cx, tile.cy) || hex("#c9c1b8"),
      age: 0,
    });
    if (this.slabs.length > 72) {
      const old = this.slabs.shift();
      if (old) this.rest(old);
    }
  }

  /**
   * Copy the rendered page under every slab, then keep only the cells each slab owns.
   * The page and text canvases are read once, into one sheet around all slabs:
   * one read per slab stalled the frame for about 5ms each.
   */
  private stampAll(tiles: PunchTile[]) {
    if (!tiles.length) return;
    const world = this.host.world;
    const z = world.Z;
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const tile of tiles) {
      x0 = Math.min(x0, tile.left);
      y0 = Math.min(y0, tile.top);
      x1 = Math.max(x1, tile.left + tile.size);
      y1 = Math.max(y1, tile.top + tile.size);
    }
    x0 = Math.floor(x0);
    y0 = Math.floor(y0);
    const sheet = document.createElement("canvas");
    sheet.width = Math.max(1, Math.ceil((x1 - x0) * z));
    sheet.height = Math.max(1, Math.ceil((y1 - y0) * z));
    const sg = sheet.getContext("2d");
    if (!sg) return;
    sg.imageSmoothingEnabled = false;
    world.pageCache.copy(sg, x0 * z, y0 * z, sheet.width, sheet.height, 0, 0, sheet.width, sheet.height);
    world.textCache.copy(sg, x0 * z, y0 * z, sheet.width, sheet.height, 0, 0, sheet.width, sheet.height);
    for (const tile of tiles) {
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.ceil(tile.size * z));
      canvas.height = Math.max(1, Math.ceil(tile.size * z));
      const g = canvas.getContext("2d");
      if (!g) continue;
      g.imageSmoothingEnabled = false;
      g.beginPath();
      for (const i of tile.cells) {
        const col = i % world.GW;
        const row = Math.floor(i / world.GW);
        g.rect((col * CELL - tile.left) * z, (row * CELL - tile.top) * z, world.pix, world.pix);
      }
      g.clip();
      g.drawImage(sheet, (tile.left - x0) * z, (tile.top - y0) * z, canvas.width, canvas.height, 0, 0, canvas.width, canvas.height);
      tile.canvas = canvas;
    }
  }

  private stepSlabs(dt: number) {
    for (let i = this.slabs.length - 1; i >= 0; i--) {
      if (!this.stepSlab(this.slabs[i], dt)) this.slabs.splice(i, 1);
    }
  }

  private stepSlab(slab: PunchSlab, dt: number): boolean {
    slab.age += dt;
    slab.vy = Math.min(1100, slab.vy + GRAVITY * dt);
    const steps = Math.max(1, Math.ceil((Math.hypot(slab.vx, slab.vy) * dt) / 8));
    const sx = (slab.vx * dt) / steps;
    const sy = (slab.vy * dt) / steps;
    for (let i = 0; i < steps; i++) {
      const nx = slab.x + sx;
      const ny = slab.y + sy;
      slab.a += (slab.av * dt) / steps;
      const speed = Math.hypot(slab.vx, slab.vy);
      if (this.solidAt(nx, ny) && !this.loose(slab.run, nx, ny)) {
        if (speed > PUNCH.carveSpeed) {
          slab.x = nx;
          slab.y = ny;
          this.crash(slab);
          return false;
        }
        slab.x = nx - sx;
        slab.y = ny - sy;
        if (speed < 90) {
          this.rest(slab);
          return false;
        }
        slab.vx *= -0.32;
        slab.vy *= -0.22;
        slab.av += (Math.random() - 0.5) * 4;
        return true;
      }
      slab.x = nx;
      slab.y = ny;
    }
    if (slab.y > this.host.world.H + 160 || slab.age > 2.6) {
      if (Math.hypot(slab.vx, slab.vy) < 140) this.rest(slab);
      return false;
    }
    return true;
  }

  /** Page that this slab's own punch is knocking loose. Slabs pass through it. */
  private loose(run: PunchRun, x: number, y: number): boolean {
    if (!run.doomed.size) return false;
    const blocks = this.host.world.blocks;
    let id = this.ownerAt(x, y);
    for (let hop = 0; id >= 0 && hop < 32; hop++) {
      if (run.doomed.has(id)) return true;
      id = blocks[id]?.parent ?? -1;
    }
    return false;
  }

  private crash(slab: PunchSlab) {
    const owner = this.ownerAt(slab.x, slab.y);
    const t = Math.max(0, Math.min(1, (slab.size - PUNCH.slabMin) / (PUNCH.slabMax - PUNCH.slabMin)));
    const radius = PUNCH.carveMin + t * (PUNCH.carveMax - PUNCH.carveMin);
    this.popCircle(slab.x, slab.y, radius + 8, slab.vx * 0.45, slab.vy * 0.45);
    if (owner >= 0) this.hurtBlock(owner, PUNCH.slabHit, slab.x, slab.y, slab.run, true);
    const world = this.host.world;
    world.beginCarve();
    world.carve(slab.x, slab.y, radius, 0.18);
    const edge = world.rim(2);
    world.char(edge.cells, edge.ring);
    const ang = Math.atan2(slab.vy, slab.vx);
    for (let i = 0; i < 6; i++) {
      const a = ang + (Math.random() - 0.5) * 1.4;
      const sp = 80 + Math.random() * 200;
      this.host.parts.chip(slab.x, slab.y, Math.cos(a) * sp, Math.sin(a) * sp - 30, slab.tint, 0.32);
    }
    if (this.smackT <= 0) {
      this.smackT = 0.045;
      this.host.sfx("hitChunk");
    }
  }

  private rest(slab: PunchSlab) {
    const piece: Slab = {
      x: slab.x,
      y: slab.y,
      a: slab.a,
      vx: slab.vx * 0.2,
      vy: Math.min(0, slab.vy),
      av: slab.av * 0.2,
      poly: [-slab.bw / 2, -slab.bh / 2, slab.bw / 2, -slab.bh / 2, slab.bw / 2, slab.bh / 2, -slab.bw / 2, slab.bh / 2],
      canvas: slab.canvas,
      bx: -slab.bw / 2,
      by: -slab.bh / 2,
      bw: slab.bw,
      bh: slab.bh,
      colors: new Uint32Array([slab.tint]),
      ccw: 1,
      cch: 1,
      m: Math.max(40, slab.bw * slab.bh),
      I: Math.max(80, slab.bw * slab.bh * 8),
      rest: 0,
      fragment: true,
      age: slab.age,
      impacts: 0,
      hitFlash: 0,
      ghost: false,
    };
    this.host.chunks.list.push(piece);
  }

  private popCircle(x: number, y: number, radius: number, vx: number, vy: number) {
    this.popBox(x - radius, y - radius, x + radius, y + radius, (lx, ly) => Math.hypot(lx - x, ly - y) <= radius + 4, vx, vy);
  }

  private popBox(
    x0: number, y0: number, x1: number, y1: number,
    hit: (x: number, y: number) => boolean,
    vx: number, vy: number,
  ) {
    const letters = this.host.letters;
    letters.query(x0, y0, x1, y1, (i) => {
      if (!letters.alive[i]) return;
      const lx = letters.x[i] + letters.w[i] / 2;
      const ly = letters.y[i] + letters.h[i] / 2;
      if (!hit(lx, ly)) return;
      const jx = vx + (Math.random() - 0.5) * 90;
      const jy = vy + (Math.random() - 0.5) * 90;
      const av = (Math.random() - 0.5) * 8;
      letters.detach(i, jx, jy, -1, false, { vx: jx, vy: jy, av, burn: -1, pinned: false });
    });
  }

  private stepFx(dt: number) {
    for (let i = this.bursts.length - 1; i >= 0; i--) {
      this.bursts[i].t += dt;
      if (this.bursts[i].t >= this.bursts[i].life) this.bursts.splice(i, 1);
    }
    for (let i = this.dusts.length - 1; i >= 0; i--) {
      this.dusts[i].t += dt;
      if (this.dusts[i].t >= this.dusts[i].life) this.dusts.splice(i, 1);
    }
  }

  private drawGlove(ctx: CanvasRenderingContext2D) {
    const tip = this.tip();
    const cos = Math.cos(this.aim);
    const sin = Math.sin(this.aim);
    const hand = this.host.peekHand();
    const cuffX = tip.x - cos * (PUNCH.glove - 2);
    const cuffY = tip.y - sin * (PUNCH.glove - 2);
    this.drawSpring(ctx, hand.x, hand.y, cuffX, cuffY);
    const squash = this.phase === "squash" ? SQUASH[Math.min(3, Math.floor(this.phaseT / (PUNCH.squash / 4)))] : { sx: 1, sy: 1 };
    const sheet = gloveSheet();
    const gh = Math.round((PUNCH.glove * sheet.height) / sheet.width);
    ctx.save();
    ctx.translate(tip.x, tip.y);
    ctx.rotate(this.aim);
    ctx.scale(squash.sx, squash.sy);
    ctx.drawImage(sheet, -PUNCH.glove, -Math.round(gh / 2), PUNCH.glove, gh);
    ctx.restore();
  }

  private tip(): { x: number; y: number } {
    const u = this.phase === "back" ? Math.min(1, this.phaseT / PUNCH.retract) : 0;
    const wob = this.phase === "back" ? Math.sin(u * Math.PI * 3.5) * (1 - u) * 14 : 0;
    const cos = Math.cos(this.aim);
    const sin = Math.sin(this.aim);
    return {
      x: this.ox + cos * this.dist - sin * wob,
      y: this.oy + sin * this.dist + cos * wob,
    };
  }

  private drawSpring(ctx: CanvasRenderingContext2D, x0: number, y0: number, x1: number, y1: number) {
    const dx = x1 - x0;
    const dy = y1 - y0;
    const len = Math.hypot(dx, dy);
    if (len < 4) return;
    const ux = dx / len;
    const uy = dy / len;
    const px = -uy;
    const py = ux;
    const pts: { x: number; y: number }[] = [{ x: x0, y: y0 }];
    for (let i = 0; i < PUNCH.coils; i++) {
      const sign = i % 2 === 0 ? 1 : -1;
      const mid = (i + 0.5) / PUNCH.coils;
      const end = (i + 1) / PUNCH.coils;
      pts.push({
        x: x0 + ux * mid * len + px * PUNCH.amp * sign,
        y: y0 + uy * mid * len + py * PUNCH.amp * sign,
      });
      pts.push({ x: x0 + ux * end * len, y: y0 + uy * end * len });
    }
    const tube = (color: string, size: number, ox: number, oy: number) => {
      for (let i = 1; i < pts.length; i++) {
        stampLine(ctx, pts[i - 1].x + ox, pts[i - 1].y + oy, pts[i].x + ox, pts[i].y + oy, color, size);
      }
    };
    tube("#1a1422", 10, 0, 0);
    tube("#3e4652", 7, px * 2.2, py * 2.2);
    tube("#8d97a6", 6, 0, 0);
    tube("#e7edf5", 3, -px * 2.4, -py * 2.4);
  }

  private drawBurst(ctx: CanvasRenderingContext2D, burst: Burst) {
    const frame = Math.min(5, Math.floor((burst.t / burst.life) * 6));
    const outer = 46 + frame * 1.4;
    const inner = outer * 0.42;
    ctx.save();
    ctx.translate(burst.x, burst.y);
    ctx.rotate(burst.a * 0.12 + frame * 0.05);
    const across = PUNCH.burst / 2;
    this.paintRing(ctx, across - 12 + frame * 0.4, 2.6, frame < 3 ? "#ffe26b" : "#ffb03a");
    this.paintRing(ctx, across - 26, 2, "#ff7a32");
    this.paintRing(ctx, across - 40, 1.5, "#e23d4a");
    this.paintStar(ctx, SPIKE.length, outer + 4, inner + 3, "#1a1422");
    this.paintStar(ctx, SPIKE.length, outer, inner, BURST_COLOR[frame]);
    this.paintStar(ctx, SPIKE.length, outer * 0.38, inner * 0.34, frame < 2 ? "#fff6d8" : "#ffb03a");
    if (burst.t < 0.018) this.paintDisc(ctx, 0, 0, 4, "#ffffff");
    ctx.fillStyle = frame < 3 ? "#fff6d8" : "#ffe26b";
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2 + frame * 0.08;
      const r0 = 18 + (i % 3) * 4;
      const r1 = 58 + (i % 4) * 3;
      stampLine(ctx, Math.cos(a) * r0, Math.sin(a) * r0, Math.cos(a) * r1, Math.sin(a) * r1, i % 2 === 0 ? "#ffe26b" : "#ff8a2a", 2);
    }
    ctx.restore();
  }

  /** A jagged annulus, so the blast reads as a ring and not a filled disc. */
  private paintRing(ctx: CanvasRenderingContext2D, radius: number, thick: number, color: string) {
    const outer = Math.ceil(radius + thick + 6);
    ctx.fillStyle = color;
    for (let y = -outer; y <= outer; y += 2) {
      for (let x = -outer; x <= outer; x += 2) {
        const ang = Math.atan2(y, x);
        const jag = 1 + 0.07 * Math.sin(ang * 8) + 0.04 * Math.sin(ang * 3);
        const d = Math.hypot(x, y);
        if (d <= (radius + thick) * jag && d >= (radius - thick) * jag) ctx.fillRect(x, y, 2, 2);
      }
    }
  }

  private paintDisc(ctx: CanvasRenderingContext2D, cx: number, cy: number, radius: number, color: string) {
    const rad = Math.ceil(radius);
    ctx.fillStyle = color;
    for (let y = -rad; y <= rad; y += 2) {
      for (let x = -rad; x <= rad; x += 2) {
        if (x * x + y * y <= radius * radius) ctx.fillRect(cx + x, cy + y, 2, 2);
      }
    }
  }

  private paintStar(ctx: CanvasRenderingContext2D, spikes: number, outer: number, inner: number, color: string) {
    const poly: number[] = [];
    for (let i = 0; i < spikes; i++) {
      const a0 = (i / spikes) * Math.PI * 2 - Math.PI / 2;
      const a1 = a0 + Math.PI / spikes;
      const jag = SPIKE[i % SPIKE.length];
      poly.push(Math.cos(a0) * outer * jag, Math.sin(a0) * outer * jag);
      poly.push(Math.cos(a1) * inner, Math.sin(a1) * inner);
    }
    const reach = Math.ceil(outer * 1.15);
    ctx.fillStyle = color;
    for (let y = -reach; y <= reach; y += 2) {
      for (let x = -reach; x <= reach; x += 2) {
        if (insidePoly(x, y, poly)) ctx.fillRect(x, y, 2, 2);
      }
    }
  }

  private drawDust(ctx: CanvasRenderingContext2D, dust: Dust) {
    const frame = Math.min(2, Math.floor((dust.t / dust.life) * 3));
    const grow = 14 + frame * 10;
    const cos = Math.cos(dust.a);
    const sin = Math.sin(dust.a);
    const ox = -sin * dust.side * grow + cos * (dust.side === 0 ? grow * 0.8 : 0);
    const oy = cos * dust.side * grow + sin * (dust.side === 0 ? grow * 0.8 : 0);
    const puffs: [number, number, number, string][] = [
      [0, 0, 9, "#e7d3b4"],
      [8, -4, 7, "#c4a27a"],
      [-7, 3, 7, "#8d6a48"],
      [4, 7, 6, "#fff6f2"],
      [-4, -7, 6, "#d9b48a"],
      [11, 2, 5, "#ffb03a"],
    ];
    ctx.save();
    ctx.globalAlpha = frame === 2 ? 0.4 : 0.92;
    const spread = 1 + frame * 0.9;
    for (const [sx, sy, size, color] of puffs) {
      const x = dust.x + ox + sx * spread;
      const y = dust.y + oy + sy * spread;
      const s = size + frame * 2;
      this.paintDisc(ctx, x, y, s + 1.4, "#1a1422");
      this.paintDisc(ctx, x, y, s, color);
      this.paintDisc(ctx, x - s * 0.2, y - s * 0.25, Math.max(2, s * 0.35), "#fff6f2");
    }
    ctx.restore();
  }
}

function insidePoly(x: number, y: number, poly: number[]): boolean {
  let hit = false;
  for (let i = 0, j = poly.length - 2; i < poly.length; j = i, i += 2) {
    const xi = poly[i];
    const yi = poly[i + 1];
    const xj = poly[j];
    const yj = poly[j + 1];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi || 0.00001) + xi) hit = !hit;
  }
  return hit;
}
